import React, { useState, useEffect, useCallback } from 'react';
import { getOrgContext } from '../utils/orgContext';

function OrgTeam() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('manager');
  const [inviting, setInviting] = useState(false);

  const orgCtx = getOrgContext();
  const token = localStorage.getItem('token');

  const fetchMembers = useCallback(async () => {
    if (!orgCtx?.organizationId) {
      setLoading(false);
      return;
    }
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/organizations/${orgCtx.organizationId}/members`, {
        headers: { Authorization: `Bearer ${token}`, 'X-Organization-Id': String(orgCtx.organizationId) }
      });
      if (!res.ok) throw new Error('Failed to load team');
      const data = await res.json();
      setMembers(data.members || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [orgCtx?.organizationId, token]);

  useEffect(() => { fetchMembers(); }, [fetchMembers]);

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    setInviting(true);
    setError('');
    setSuccess('');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/organizations/${orgCtx.organizationId}/members`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'X-Organization-Id': String(orgCtx.organizationId)
        },
        body: JSON.stringify({ email: inviteEmail.trim(), role: inviteRole })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to invite');
      setSuccess(`Invited ${inviteEmail.trim()}`);
      setInviteEmail('');
      fetchMembers();
    } catch (err) {
      setError(err.message);
    } finally {
      setInviting(false);
    }
  };

  const handleRemove = async (memberId) => {
    if (!window.confirm('Remove this team member?')) return;
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/organizations/${orgCtx.organizationId}/members/${memberId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}`, 'X-Organization-Id': String(orgCtx.organizationId) }
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to remove');
      }
      setSuccess('Member removed.');
      fetchMembers();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleRoleChange = async (memberId, newRole) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/organizations/${orgCtx.organizationId}/members/${memberId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'X-Organization-Id': String(orgCtx.organizationId)
        },
        body: JSON.stringify({ role: newRole })
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to update role');
      }
      fetchMembers();
    } catch (err) {
      setError(err.message);
    }
  };

  const roleLabel = (role) => {
    const labels = { owner: 'Owner', manager: 'Manager', property_owner: 'Property Owner' };
    return labels[role] || role;
  };

  const statusBadge = (status) => {
    if (status === 'active') return <span className="report-chip report-chip--green">Active</span>;
    if (status === 'pending') return <span className="report-chip report-chip--muted">Pending</span>;
    return <span className="report-chip">{status}</span>;
  };

  if (loading) return <main className="App-main"><p>Loading...</p></main>;
  if (!orgCtx?.organizationId) {
    return (
      <main className="App-main">
        <div className="report-card" style={{ maxWidth: 600, margin: '2rem auto', padding: '2rem' }}>
          <h2>Team Members</h2>
          <p className="status-text">You are not part of an organization. Register as a Property Manager to create one.</p>
        </div>
      </main>
    );
  }

  return (
    <main className="App-main">
      <div style={{ maxWidth: 720, margin: '2rem auto', padding: '0 1rem' }}>
        <h2 style={{ marginBottom: '0.25rem' }}>Team Members</h2>
        <p className="status-text" style={{ marginBottom: '1.5rem' }}>Manage who has access to your organization.</p>

        {error && <div className="form-error" style={{ marginBottom: '1rem' }}>{error}</div>}
        {success && <div className="form-success" style={{ marginBottom: '1rem' }}>{success}</div>}

        {/* Invite form */}
        <div className="report-card" style={{ padding: '1.5rem', marginBottom: '1.5rem' }}>
          <h3 style={{ marginBottom: '0.75rem' }}>Invite team member</h3>
          <form onSubmit={handleInvite} style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div className="form-group" style={{ flex: '1 1 200px', marginBottom: 0 }}>
              <label>Email</label>
              <input
                type="email"
                className="form-control"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="colleague@company.com"
                required
              />
            </div>
            <div className="form-group" style={{ flex: '0 0 140px', marginBottom: 0 }}>
              <label>Role</label>
              <select className="form-control" value={inviteRole} onChange={(e) => setInviteRole(e.target.value)}>
                <option value="manager">Manager</option>
                <option value="property_owner">Property Owner</option>
              </select>
            </div>
            <button type="submit" className="primary-button" disabled={inviting} style={{ height: 38 }}>
              {inviting ? 'Inviting...' : 'Send invite'}
            </button>
          </form>
        </div>

        {/* Members list */}
        <div className="report-card" style={{ padding: '1.5rem' }}>
          <h3 style={{ marginBottom: '1rem' }}>Current members ({members.length})</h3>
          {members.length === 0 ? (
            <p className="status-text">No team members yet. Invite someone above.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {members.map((m) => (
                <div key={m.id} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '0.75rem 1rem', borderRadius: 8,
                  border: '1px solid var(--border-color, #e5e7eb)',
                  background: 'var(--surface-color, #fff)',
                  flexWrap: 'wrap', gap: '0.5rem'
                }}>
                  <div>
                    <strong>{m.first_name && m.last_name ? `${m.first_name} ${m.last_name}` : m.email}</strong>
                    {m.first_name && <span className="status-text" style={{ marginLeft: '0.5rem' }}>{m.email}</span>}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    {statusBadge(m.status)}
                    {m.role === 'owner' ? (
                      <span className="report-chip">{roleLabel(m.role)}</span>
                    ) : (
                      <select
                        className="form-control"
                        value={m.role}
                        onChange={(e) => handleRoleChange(m.id, e.target.value)}
                        style={{ width: 'auto', padding: '0.25rem 0.5rem', fontSize: '0.85rem' }}
                      >
                        <option value="manager">Manager</option>
                        <option value="property_owner">Property Owner</option>
                      </select>
                    )}
                    {m.role !== 'owner' && (
                      <button
                        onClick={() => handleRemove(m.id)}
                        className="ghost-button"
                        style={{ padding: '0.25rem 0.5rem', fontSize: '0.8rem', color: 'var(--error-color, #ef4444)' }}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </main>
  );
}

export default OrgTeam;
